import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { ChampDeclare } from '../views/fields.js';
import type { EcranDeclare } from '../views/screens.js';

/**
 * LES APPS INSTANCIEES PAR L'ORG, telles que le socle les lit.
 *
 * UNE APP EST UNE DECLARATION INTERPRETEE AU RUNTIME (`docs/CHANTIERS-SOCLE.md`,
 * chantier `app-declaration-par-org`). Le bundle porte la declaration de
 * reference de chaque app, son MODELE ; quand un client la personnalise, sa
 * declaration ENTIERE est copiee dans un document Firestore, `orgs/{orgId}/apps/{appId}`,
 * et c'est ce document que l'ecran generique interprete a la place du modele.
 * Pas d'instance = l'app tourne sur son modele.
 *
 * POURQUOI UN PORT, comme le branding (`lib/branding.tsx`) et l'acces
 * (`lib/acces.tsx`) : `@taki/ui` et le shell ne parlent jamais au SDK. L'hote
 * observe la collection par `sdk.appsInstanciees` et monte
 * `DeclarationsProvider` autour du shell ; sans provider, toute app tourne sur
 * son modele, exactement comme avant ce chantier. Deux lecteurs s'en servent :
 * le SHELL (le nom et le libelle de menu que le client a renommes, le panneau
 * Parametres qui edite l'instance) et l'ECRAN GENERIQUE de `@taki/app-runtime`
 * (la declaration a interpreter). Le port vit ici, en amont des deux.
 *
 * POURQUOI LES TYPES SONT STRUCTURELS et non importes de `@taki/data-model` :
 * meme raison que `ChampDeclare` et `EcranDeclare`, ce package est le design
 * system et ne connait aucun schema Zod. Les fichiers de declaration
 * (`FichierDeChamps`, `FichierDEcrans`) sont decrits par les formes que les
 * vues consomment deja ; le SDK, qui valide par Zod, rend des valeurs qui ne
 * compilent contre ces formes QUE si elles concordent.
 */

/**
 * LA FICHE INSTANCIEE : le sous-ensemble de `taki.app.json` qu'un client peut
 * RENOMMER. Jamais l'id, la version, les entites ni les capacites. La meme
 * liste fermee est ecrite dans les rules et dans le schema du SDK.
 */
export interface FicheInstanciee {
  name?: string;
  description?: string;
  icon?: string;
  menu?: { label?: string };
}

/** Un jeu declare : la famille, son type local, ses champs du sac. */
export interface JeuDeclare {
  family: string;
  type?: string;
  nom?: string;
  champs: readonly ChampDeclare[];
}

/** Les familles réellement disponibles, annoncées par l'hôte au Studio. */
export interface FamilleDeDeclaration {
  family: string;
  /** Présent lorsque le sous-type appartient à un vocabulaire fermé. */
  types?: readonly string[];
  typeRequis?: boolean;
  natifs: readonly {
    key: string;
    genre: string;
    requis: boolean;
    valeurs?: readonly string[];
    /**
     * Pose par le socle, jamais par une saisie (`createdBy`, `createdAt`...) :
     * le Studio le montre, il ne lui donne pas d'editeur.
     */
    lectureSeule?: boolean;
  }[];
}

/** Le contenu d'un `taki.fields.json`, ou de la partie `champs` d'une instance. */
export interface FichierDeChamps {
  version: 1;
  jeux: readonly JeuDeclare[];
}

/** Le contenu d'un `taki.screens.json`, ou de la partie `ecrans` d'une instance. */
export interface FichierDEcrans {
  version: 1;
  ecrans: readonly EcranDeclare[];
}

/**
 * UNE DECLARATION COMPLETE : les champs, les ecrans, et ce que la fiche
 * renomme. C'est la forme commune du modele et de l'instance, et ce que
 * l'ecran generique interprete, d'ou qu'elle vienne.
 */
export interface DeclarationDApp {
  champs: FichierDeChamps;
  ecrans: FichierDEcrans;
  fiche?: FicheInstanciee;
}

/** Le cliche du modele copie dans l'instance : une declaration, plus la version de l'app qui la portait. */
export interface ModeleDApp extends DeclarationDApp {
  version: string;
}

/** Une instance lue : la declaration du client, son cliche de base, et qui l'a touchee en dernier. */
export interface InstanceDApp extends DeclarationDApp {
  appId: string;
  modele: ModeleDApp;
  /** `null` tant que le serveur n'a pas resolu l'horodatage. */
  updatedAt: Date | null;
  updatedBy: string;
}

/** Ce qu'on enregistre : la declaration du client et le cliche du modele qu'elle etend. */
export interface InstanceAEnregistrer extends DeclarationDApp {
  modele: ModeleDApp;
}

/**
 * Un document present mais INUTILISABLE (schema refuse, app du chemin qui ne
 * correspond pas). L'app retombe sur son modele ; Parametres le dit, avec la
 * raison, au lieu de laisser croire que la personnalisation a disparu.
 */
export interface InstanceIllisible {
  appId: string;
  raison: string;
}

/**
 * CE QUE LA VALIDATION REND : la declaration relue, ou la raison du refus, en
 * francais. C'est le MEME couple que l'ecran generique emploie pour choisir
 * entre l'instance et le modele (`resoudreDeclaration`, `@taki/app-runtime`) :
 * une declaration qui ne se relit pas ne s'ecrit pas.
 */
export type ValidationDeclaree =
  | { ok: true; declaration: DeclarationDApp }
  | { ok: false; raison: string };

/** Un changement que le modele apporte et que le client n'avait pas touche. */
export interface ChangementDeModele {
  /** Le chemin du point de fusion (`ecrans/ecrans[principal]/portes[personnes]/label`). */
  chemin: string;
  genre: 'ajoute' | 'retire' | 'modifie';
}

/** Un point ou le modele et le client ont bouge chacun de leur cote. */
export interface ConflitDeFusion {
  chemin: string;
  /** La valeur du cliche de base, `undefined` quand cette voie n'a rien ici. */
  base?: unknown;
  /** La valeur du modele courant. */
  modele?: unknown;
  /** La valeur du client. */
  instance?: unknown;
  /** Ce que CETTE fusion a retenu, une fois les choix appliques. */
  garde: 'instance' | 'modele';
}

/**
 * LE RESULTAT D'UNE FUSION A TROIS VOIES, tel que le panneau de mise a jour le
 * lit. `resultat` est `null` quand la declaration fusionnee ne se relit pas :
 * `refus` dit alors pourquoi, et les changements comme les conflits restent
 * rendus (l'ecran doit pouvoir dire ce qui arrive ET pourquoi ca bloque).
 */
export interface FusionDeclaree {
  resultat: DeclarationDApp | null;
  changements: readonly ChangementDeModele[];
  conflits: readonly ConflitDeFusion[];
  refus: string | null;
}

/** Ce que le panneau de mise a jour a tranche, par chemin. Absent = l'instance. */
export type ChoixDeConflit = Readonly<Record<string, 'modele' | 'instance'>>;

export interface DeclarationsPort {
  optionsChamp?(input: object): Promise<Record<string, unknown>>;
  /** Catalogue du SDK, pour créer un jeu sans inventer une famille. */
  familles?: readonly FamilleDeDeclaration[];
  /**
   * Vrai des que la premiere lecture est arrivee (ou a echoue) : avant, on ne
   * sait pas encore si l'org a des instances, et monter un ecran sur le modele
   * pour le remplacer une seconde plus tard ferait sauter la fenetre.
   */
  pret: boolean;
  /** Les instances lisibles, par id d'app. */
  instances: ReadonlyMap<string, InstanceDApp>;
  /** Les documents presents mais illisibles, avec leur raison. */
  illisibles: readonly InstanceIllisible[];
  /** Ce qui empeche de lire la collection, s'il y a lieu (reseau, rules). `null` sinon. */
  erreur: string | null;
  /** Le MEME droit que le branding et la gestion des membres : owner ou admin actif. */
  peutModifier: boolean;
  /** Ecrit la declaration ENTIERE d'une app (la premiere fois : cree l'instance). */
  enregistrer(appId: string, instance: InstanceAEnregistrer): Promise<void>;
  /** Revient au modele : supprime l'instance, l'app retombe sur la declaration du bundle. */
  reinitialiser(appId: string): Promise<void>;
  /**
   * RELIT UNE DECLARATION PAR LES LECTEURS DU SOCLE, avant toute ecriture.
   *
   * POURQUOI L'HOTE LA FOURNIT, comme `enregistrer` : les lecteurs sont des
   * schemas Zod de `@taki/data-model`, que ni `@taki/ui` ni `@taki/shell`
   * n'ont dans leurs dependances (meme raison que les types structurels de
   * `ChampDeclare` : le design system ne connait aucun schema). Le studio
   * client, lui, DOIT valider avant d'ecrire, sinon c'est le client qui devient
   * le chemin par lequel une declaration fausse entre en base.
   */
  valider(declaration: DeclarationDApp, appId?: string): ValidationDeclaree;
  /**
   * LA FUSION A TROIS VOIES, quand le bundle livre un nouveau modele : le
   * cliche de base de l'instance, le modele courant, la declaration du client.
   * Fournie par l'hote pour la meme raison que `valider` : elle vit dans
   * `@taki/data-model` (`fusionnerDeclarations`), elle est pure, et elle relit
   * son propre resultat.
   */
  fusionner(
    base: DeclarationDApp,
    modele: DeclarationDApp,
    instance: DeclarationDApp,
    choix?: ChoixDeConflit,
  ): FusionDeclaree;
}

/** L'instance d'une app, ou `null` : sans provider, sans document, ou document illisible. */
export function instanceDe(port: DeclarationsPort | null, appId: string): InstanceDApp | null {
  return port?.instances.get(appId) ?? null;
}

const DeclarationsContext = createContext<DeclarationsPort | null>(null);

export function DeclarationsProvider({
  value,
  children,
}: {
  value: DeclarationsPort;
  children: ReactNode;
}) {
  return <DeclarationsContext.Provider value={value}>{children}</DeclarationsContext.Provider>;
}

/** Le port des declarations, ou `null` sans provider (toute app tourne alors sur son modele). */
export function useDeclarationsPort(): DeclarationsPort | null {
  return useContext(DeclarationsContext);
}
